import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, ShieldCheck, ShieldOff, UserPlus, Users } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/form-classes';
import {
  ACCESS_LEVELS,
  ACCESS_LEVEL_DESCRIPTIONS,
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVEL_ROLES,
  ROLE_ACCESS_LEVELS,
} from '../../core/access-levels';
import type { AccessLevel } from '../../core/access-levels';
import { DEFAULT_ADMIN_PERMISSIONS, PERMISSION_LABELS, SUPER_ADMIN_ONLY, hasPermission } from '../../core/permissions';
import { formatPhone } from '../../core/phone';
import { isValidPin } from '../../core/pin';
import { SUPER_ADMIN_ID } from '../../core/mock-users';
import {
  createTeamMember,
  describeAccess,
  listUsers,
  resetUserPin,
  setPermissionOverride,
  setUserApproval,
  setUserRole,
} from '../../db/auth-store';
import { USER_PERMISSIONS } from '../../types/user';
import type { User, UserPermission } from '../../types/user';

interface UserAccessModalProps {
  /** The signed-in מנהל על — every write is attributed to them. */
  user: User;
  onClose: () => void;
  /** A directory change may have touched the operator's own account. */
  onChanged?: () => void;
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

/** The flags a מנהל על may tune on a commander's account, in reading order. */
const TUNABLE_PERMISSIONS: readonly UserPermission[] = USER_PERMISSIONS.filter(
  (permission) => !SUPER_ADMIN_ONLY.includes(permission),
);

const BLANK_DRAFT = {
  fullName: '',
  phone: '',
  personalNumber: '',
  level: 'driver' as AccessLevel,
  pin: '',
};

/**
 * "ניהול הרשאות ומשתמשים" — the מנהל על's directory.
 *
 * This is the one screen in the application that can open an account, move
 * somebody between access levels, switch their access off, or set the four-digit
 * code they sign in with. It is reached only by an account holding
 * `MANAGE_USERS`, which is super-admin-only — and every control is *also*
 * guarded inside `src/db/auth-store.ts`, which is what actually protects the
 * directory. The screen only decides what to offer.
 *
 * The owner's own row is deliberately inert: demoting or suspending the only
 * account that can reach this screen would lock the unit out of its own
 * directory with no way back, so the store refuses it and the row says so.
 */
export function UserAccessModal({ user, onClose, onChanged }: UserAccessModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pinDrafts, setPinDrafts] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK_DRAFT);

  const canManage = hasPermission(user, 'MANAGE_USERS');
  const canResetPin = hasPermission(user, 'RESET_USER_PIN');

  const load = useCallback(async () => {
    const directory = await listUsers();
    setUsers(
      [...directory].sort((a, b) => {
        // The owner leads, then commanders, then drivers — and inside each
        // level, by name, so the list reads like the chain of command.
        const rank = (entry: User) => ACCESS_LEVELS.indexOf(ROLE_ACCESS_LEVELS[entry.role]);
        return rank(a) - rank(b) || a.fullName.localeCompare(b.fullName, 'he');
      }),
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Runs one directory write, reporting whatever it says in Hebrew. */
  const run = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      await load();
      onChanged?.();
      setNotice({ tone: 'ok', text: label });
    } catch (error) {
      setNotice({ tone: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (draft.pin && !isValidPin(draft.pin)) {
      setNotice({ tone: 'error', text: 'קוד הכניסה חייב להיות 4 ספרות' });
      return;
    }
    await run(`${draft.fullName.trim()} נוסף למערכת.`, async () => {
      await createTeamMember(user, {
        fullName: draft.fullName,
        phone: draft.phone,
        personalNumber: draft.personalNumber,
        role: ACCESS_LEVEL_ROLES[draft.level],
        company: user.company,
        pin: draft.pin || undefined,
      });
      setDraft(BLANK_DRAFT);
      setAdding(false);
    });
  };

  const handlePinReset = async (subject: User) => {
    const pin = pinDrafts[subject.id] ?? '';
    if (!isValidPin(pin)) {
      setNotice({ tone: 'error', text: 'קוד הכניסה חייב להיות 4 ספרות' });
      return;
    }
    await run(`קוד הכניסה של ${subject.fullName} עודכן.`, async () => {
      await resetUserPin(user, subject.id, pin);
      setPinDrafts((current) => ({ ...current, [subject.id]: '' }));
    });
  };

  const counts = useMemo(() => {
    const tally = { super_admin: 0, role_holder: 0, driver: 0 } as Record<AccessLevel, number>;
    for (const entry of users) tally[ROLE_ACCESS_LEVELS[entry.role]] += 1;
    return tally;
  }, [users]);

  if (!canManage) {
    return (
      <Modal title="ניהול הרשאות ומשתמשים" onClose={onClose}>
        <p className="text-sm text-ink/60">
          המסך שמור למנהל על בלבד. ניהול המשתמשים, איפוס קודי הכניסה והגדרות המערכת אינם זמינים
          לבעלי תפקידים.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      size="lg"
      title="ניהול הרשאות ומשתמשים"
      subtitle={`${users.length} חשבונות · ${counts.super_admin} מנהל על · ${counts.role_holder} בעלי תפקידים · ${counts.driver} נהגים`}
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-4 rounded-xl border border-line text-sm"
        >
          סגירה
        </button>
      }
    >
      <div className="space-y-4">
        {notice ? (
          <p
            className={[
              'rounded-xl border p-3 text-sm',
              notice.tone === 'ok'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-rose-300 bg-rose-50 text-rose-700',
            ].join(' ')}
            role="status"
          >
            {notice.text}
          </p>
        ) : null}

        <section className="rounded-2xl border border-line p-4">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
            <Users size={16} className="text-brand-500" aria-hidden />
            רמות ההרשאה במערכת
          </h3>
          <dl className="space-y-1.5">
            {ACCESS_LEVELS.map((level) => (
              <div key={level} className="text-xs">
                <dt className="font-medium">{ACCESS_LEVEL_LABELS[level]}</dt>
                <dd className="text-ink/60">{ACCESS_LEVEL_DESCRIPTIONS[level]}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setAdding((current) => !current)}
            data-add-member
            className="h-9 px-3 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-1.5"
          >
            <UserPlus size={15} aria-hidden />
            הוספת משתמש
          </button>
        </div>

        {adding ? (
          <section className="rounded-2xl border border-brand-100 bg-brand-50 p-4 space-y-3">
            <h3 className="font-semibold text-sm">פתיחת חשבון לחבר צוות</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-xs text-ink/50">שם מלא</span>
                <input
                  className={fieldClass}
                  value={draft.fullName}
                  onChange={(event) => setDraft({ ...draft, fullName: event.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="text-xs text-ink/50">טלפון</span>
                <input
                  className={fieldClass}
                  data-dir="ltr"
                  inputMode="tel"
                  value={draft.phone}
                  onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="text-xs text-ink/50">מספר אישי</span>
                <input
                  className={fieldClass}
                  data-dir="ltr"
                  inputMode="numeric"
                  value={draft.personalNumber}
                  onChange={(event) => setDraft({ ...draft, personalNumber: event.target.value })}
                />
              </label>
              <label className="text-sm">
                <span className="text-xs text-ink/50">רמת הרשאה</span>
                <select
                  className={fieldClass}
                  value={draft.level}
                  onChange={(event) =>
                    setDraft({ ...draft, level: event.target.value as AccessLevel })
                  }
                >
                  {ACCESS_LEVELS.filter((level) => level !== 'super_admin').map((level) => (
                    <option key={level} value={level}>
                      {ACCESS_LEVEL_LABELS[level]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="text-xs text-ink/50">קוד כניסה (4 ספרות, רשות)</span>
                <input
                  className={fieldClass}
                  data-dir="ltr"
                  inputMode="numeric"
                  maxLength={4}
                  value={draft.pin}
                  onChange={(event) =>
                    setDraft({ ...draft, pin: event.target.value.replace(/\D/g, '').slice(0, 4) })
                  }
                />
              </label>
            </div>
            <p className="text-xs text-ink/60">
              ללא קוד — החבר יבחר קוד משלו בכניסה הראשונה. המכשיר שלו עדיין טעון אישור בנפרד.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="h-9 px-3 rounded-xl border border-line text-sm"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleAdd()}
                className="h-9 px-3 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40"
              >
                פתיחת חשבון
              </button>
            </div>
          </section>
        ) : null}

        <ul className="space-y-2" data-user-directory>
          {users.map((subject) => {
            const level = ROLE_ACCESS_LEVELS[subject.role];
            const isOwner = subject.id === SUPER_ADMIN_ID;
            const active = subject.isApproved && subject.status === 'active';
            const open = expanded === subject.id;

            return (
              <li key={subject.id} className="rounded-2xl border border-line p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {subject.fullName}
                      <span className="text-ink/50 text-sm"> · {ACCESS_LEVEL_LABELS[level]}</span>
                    </p>
                    <p className="text-xs text-ink/50" data-dir="ltr">
                      {formatPhone(subject.phone)} · {subject.personalNumber}
                    </p>
                  </div>
                  <span
                    data-access={active ? 'active' : 'suspended'}
                    className={[
                      'shrink-0 text-xs px-2 py-1 rounded-lg border',
                      active
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-rose-200 bg-rose-50 text-rose-700',
                    ].join(' ')}
                  >
                    {describeAccess(subject)}
                  </span>
                </div>

                {isOwner ? (
                  <p className="mt-2 text-xs text-ink/50">
                    חשבון מנהל העל — רמת ההרשאה והגישה שלו נעולות, כדי שלא ניתן יהיה לנעול את
                    היחידה מחוץ למערכת.
                  </p>
                ) : (
                  <>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <label className="text-sm">
                        <span className="text-xs text-ink/50 me-1.5">רמת הרשאה</span>
                        <select
                          className={`${fieldClass} w-auto inline-block h-9`}
                          value={level}
                          disabled={busy}
                          data-role-select={subject.id}
                          onChange={(event) =>
                            void run(
                              `${subject.fullName} — רמת ההרשאה עודכנה.`,
                              () =>
                                setUserRole(
                                  user,
                                  subject.id,
                                  ACCESS_LEVEL_ROLES[event.target.value as AccessLevel],
                                ),
                            )
                          }
                        >
                          {ACCESS_LEVELS.map((option) => (
                            <option key={option} value={option}>
                              {ACCESS_LEVEL_LABELS[option]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <button
                        type="button"
                        disabled={busy}
                        data-toggle-access={subject.id}
                        onClick={() =>
                          void run(
                            `${subject.fullName} — הגישה ${active ? 'הושבתה' : 'הופעלה'}.`,
                            () => setUserApproval(user, subject.id, !active),
                          )
                        }
                        className={[
                          'h-9 px-3 rounded-xl border text-sm flex items-center gap-1.5 disabled:opacity-40',
                          active
                            ? 'border-rose-200 bg-rose-50 text-rose-700'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700',
                        ].join(' ')}
                      >
                        {active ? <ShieldOff size={15} aria-hidden /> : <ShieldCheck size={15} aria-hidden />}
                        {active ? 'השבתת גישה' : 'הפעלת גישה'}
                      </button>

                      {subject.role === 'admin' ? (
                        <button
                          type="button"
                          onClick={() => setExpanded(open ? null : subject.id)}
                          className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
                        >
                          {open ? 'סגירת הרשאות פרטניות' : 'הרשאות פרטניות'}
                        </button>
                      ) : null}
                    </div>

                    {canResetPin ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <label className="text-sm">
                          <span className="text-xs text-ink/50 me-1.5">קוד כניסה חדש</span>
                          <input
                            className={`${fieldClass} w-24 inline-block h-9`}
                            data-dir="ltr"
                            inputMode="numeric"
                            maxLength={4}
                            data-pin-input={subject.id}
                            value={pinDrafts[subject.id] ?? ''}
                            onChange={(event) =>
                              setPinDrafts((current) => ({
                                ...current,
                                [subject.id]: event.target.value.replace(/\D/g, '').slice(0, 4),
                              }))
                            }
                          />
                        </label>
                        <button
                          type="button"
                          disabled={busy}
                          data-reset-pin={subject.id}
                          onClick={() => void handlePinReset(subject)}
                          className="h-9 px-3 rounded-xl border border-line text-sm flex items-center gap-1.5 disabled:opacity-40"
                        >
                          <KeyRound size={15} aria-hidden />
                          איפוס קוד
                        </button>
                      </div>
                    ) : null}

                    {open && subject.role === 'admin' ? (
                      <ul className="mt-3 grid sm:grid-cols-2 gap-1.5">
                        {TUNABLE_PERMISSIONS.map((permission) => {
                          const override = subject.permissionOverrides?.[permission];
                          const granted = override ?? DEFAULT_ADMIN_PERMISSIONS.includes(permission);
                          return (
                            <li key={permission}>
                              <label className="flex items-center gap-2 text-xs rounded-xl border border-line px-2.5 py-2">
                                <input
                                  type="checkbox"
                                  checked={granted}
                                  disabled={busy}
                                  onChange={(event) =>
                                    void run(
                                      `${subject.fullName} — ההרשאה עודכנה.`,
                                      () =>
                                        setPermissionOverride(
                                          user,
                                          subject.id,
                                          permission,
                                          event.target.checked,
                                        ),
                                    )
                                  }
                                />
                                <span className="min-w-0 truncate">
                                  {PERMISSION_LABELS[permission]}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
